'use client';
import { FacebookShareButton, PinterestShareButton, TwitterShareButton } from 'react-share';
import BreadcrumbSeo from '@repo/ui/src/components/Seo/_BreadcrumbSEO';
import { useBreadcrumbs } from '@repo/ui/src/context/BreadCrumbProvider';
import buildLinkObj from '@repo/ui/src/functions/build-link';
import { buildImageSrc } from '@repo/ui/src/functions/image-functions-aspect-ratio';
import PinterestIcon from '@repo/ui/src/components/Icons/PinterestIcon';
import FacebookIcon from '@repo/ui/src/components/Icons/FacebookIcon';
import TwitterIcon from '@repo/ui/src/components/Icons/TwitterIcon';
import HeaderPaddingWrapper from '@repo/ui/src/components/HeaderPaddingWrapper';
import { classNames } from '@repo/ui/src/utils/classNames';
import { Anchor } from '@repo/ui/src/components/Anchor';
import { LinkProps } from '@repo/ui/src/types/link';
import { SeoProps } from '@repo/ui/src/types/seo';
import useCurrentUrl from '@repo/ui/src/hooks/useCurrentUrl';

interface Props {
  breadcrumb?: Array<LinkProps>;
  showSocial?: boolean;
  seoObj?: SeoProps;
  className?: string;
  title?: string;
  dark?: boolean;
}

const iconProps = {
  backFillColor: '#25282a',
  frontFillColor: '#fff',
};

const Breadcrumb = ({ breadcrumb, showSocial, seoObj, className, title, dark }: Props) => {
  const { path } = useCurrentUrl();
  const { darkBackground } = useBreadcrumbs();

  if (!breadcrumb?.length) {
    return null;
  }

  const imgUrl = buildImageSrc(seoObj?.ogImage, 600, 1, 1);
  const pageUrl = `${process.env.NEXT_PUBLIC_WEB_ENV}/${path}`;

  return (
    <HeaderPaddingWrapper
      className={classNames(
        'tw-hidden tw-py-8 tw-absolute tw-z-[102]',
        showSocial ? 'sm:tw-flex tw-justify-between tw-w-full' : 'sm:tw-inline-block',
        className,
      )}
    >
      <BreadcrumbSeo breadcrumbs={breadcrumb.map(buildLinkObj)} />
      <nav aria-label="breadcrumb">
        <ol
          className={classNames(
            'tw-list-none tw-grid tw-grid-flow-col tw-items-baseline tw-pl-0 tw-gap-x-1.5',
            (dark || darkBackground) && 'tw-text-white',
          )}
        >
          {breadcrumb?.map((link, i) => (
            <li
              key={i}
              className={classNames(
                "tra-text-web-body-m tw-text-inherit tw-text-xs tw-grid tw-grid-flow-col tw-items-baseline tw-gap-x-1.5 after:tw-content-['/'] after:tw-inline-block",
                i === breadcrumb.length - 1 && !title && 'after:tw-content-none',
              )}
            >
              <Anchor
                {...link}
                className="tra-text-web-body-m tw-text-xs tw-text-inherit hover:tw-underline"
              >
                {link?.displayName}
              </Anchor>
            </li>
          ))}
          {title && (
            <li
              key="pageTitle"
              className={classNames('tra-text-web-body-m tw-text-xs tw-text-inherit')}
              itemProp="name"
            >
              {title}
            </li>
          )}
        </ol>
      </nav>
      {showSocial && (
        <div className="tw-grid tw-grid-flow-col tw-gap-x-4">
          {seoObj?.ogImage && (
            <PinterestShareButton media={imgUrl} url={pageUrl}>
              <PinterestIcon {...iconProps} />
            </PinterestShareButton>
          )}
          <TwitterShareButton url={pageUrl}>
            <TwitterIcon {...iconProps} />
          </TwitterShareButton>
          <FacebookShareButton url={pageUrl}>
            <FacebookIcon {...iconProps} />
          </FacebookShareButton>
        </div>
      )}
    </HeaderPaddingWrapper>
  );
};

export default Breadcrumb;
